/**
 * Widget copy in English and Dutch. The language follows `init({ locale })`, or the host page's
 * `<html lang>` (MMS is `nl`), and falls back to English.
 */
const en = {
  feedback: 'Feedback',
  close: 'Close',
  tabNew: 'New',
  tabMine: 'My feedback',
  pins: 'Pins',
  unreadReplies: (n: number) => (n === 1 ? '1 new reply' : `${n} new replies`),

  // Identity
  whoTitle: 'Who is giving feedback?',
  whoIntro:
    'We use your email to show you the status of what you send, and to let you know when the team replies.',
  name: 'Name',
  workEmail: 'Work email',
  continue: 'Continue',
  savedLocally: 'Saved in this browser only. You can change it later at the bottom of the sidebar.',
  privacy:
    'Your name and email are visible to the Hoppinger team and the project team of this product.',
  privacyLink: 'Privacy statement',
  cancel: 'Cancel',
  change: 'Change',
  emailInvalid: 'Enter a full email address.',
  pickWho: 'Pick who is giving feedback in this browser, or add someone.',
  continueAs: 'Continue as',
  someoneElse: 'Someone else',
  forget: 'Remove from this browser',
  optional: 'Optional',
  namePlaceholder: 'Filled in from your email',
  nameHint: 'Filled in from your email. The team can correct it.',
  viewLarger: 'Click to view larger',
  closeImage: 'Close image',
  captureFailed: 'Screenshot failed. Paste an image instead.',
  retry: 'Try again',

  // New report
  reportBug: 'Report a bug',
  requestFeature: 'Request a feature',
  summary: 'Summary',
  required: 'Required',
  summaryPlaceholderBug: 'For example: Save changes does nothing',
  summaryPlaceholderFeature: 'What would you like to be able to do?',
  summaryMissing: 'Add a short summary so the team can find your report.',
  story: 'Story',
  yourIdea: 'Your idea',
  draftSaved: 'Draft saved',
  storyPlaceholderBug:
    'Tell the story: what you did, what happened and what you expected. Add screenshots along the way.',
  storyPlaceholderFeature:
    'Describe what you would like to be able to do and why it helps you. Add screenshots to show where.',
  continueStory: 'Continue the story…',
  bold: 'Bold',
  italic: 'Italic',
  link: 'Link',
  linkAdd: 'Add',
  linkClose: 'Close link field',
  linkHint: 'Enter adds the link, Esc closes the field and returns focus to the text.',
  element: 'Element',
  area: 'Area',
  screenshot: 'Screenshot',
  storyHint: 'Screenshots are added where your cursor is. You can also paste (⌘V) or drop images.',
  limitReached: (max: number) =>
    `You have added ${max} images, the maximum. Remove one to add another.`,
  remove: 'Remove',
  pastedImage: 'Pasted image',
  elementLabel: 'Element:',
  areaLabel: 'Area:',
  takingScreenshot: 'Taking screenshot…',
  pickElementInline: 'Click the element on the page.',
  pickAreaInline: 'Drag over the page to select an area.',
  landsHere: 'The screenshot lands here. Esc to cancel.',
  dropTitle: 'Drop to add to your story',
  dropSub: (left: number, max: number) => `Added at the cursor · ${left} of ${max} left`,
  imageUnreadable: 'This image could not be read. Try a PNG or JPEG.',
  screenshotFailed: 'The screenshot could not be made. You can still send your feedback.',
  techTitle: 'Technical details that will be sent',
  techSummary: (errors: number, failed: number, actions: number) =>
    `Page, browser and screen size · ${errors} console error${errors === 1 ? '' : 's'} · ${failed} failed request${failed === 1 ? '' : 's'} · ${actions} recent action${actions === 1 ? '' : 's'}`,
  send: 'Send feedback',
  sending: 'Sending…',
  sendFailedTitle: 'Your feedback was not sent.',
  sendFailedBody: 'The connection dropped. Your draft and screenshots are kept.',
  tryAgain: 'Try again',

  // Sent
  sentTitle: 'Thanks, your feedback has been sent.',
  sentBody: 'The team will look at it. If they have a question, it shows up under My feedback.',
  justNow: 'Just now',
  screenshots: (n: number) => (n === 1 ? '1 screenshot' : `${n} screenshots`),
  view: 'View',
  sendMore: 'Send more',

  // Lists and conversation
  bug: 'Bug',
  idea: 'Idea',
  reportsCount: (n: number) => (n === 1 ? '1 report' : `${n} reports`),
  refresh: 'Refresh',
  emptyTitle: 'You have not sent any feedback yet.',
  emptyBody: "What you send from this browser shows up here, with the team's replies.",
  writeFeedback: 'Write feedback',
  loadFailedTitle: 'Your feedback could not be loaded.',
  loadFailedBody: 'Check your connection and try again. Nothing you sent is lost.',
  back: 'My feedback',
  sent: 'Sent',
  whatYouSent: 'What you sent',
  noMessagesTitle: 'No messages yet.',
  noMessagesBody: 'The team can ask you questions here, and you can add details.',
  you: 'You',
  newDivider: 'New',
  reply: 'Reply',
  replyPlaceholder: 'Answer a question or add details',
  toSend: 'to send',
  sendReply: 'Send reply',
  replyFailed: 'Your reply could not be sent. Try again.',

  // Picking
  bannerElement: 'Click the element you want to report',
  bannerArea: 'Drag to select an area of the screen',
  bannerTapElement: 'Tap the element to report',
  bannerTapAgain: 'Tap again to confirm',
  bannerTapArea: 'Drag over the page to select an area',
  esc: 'Esc',
  toCancel: 'to cancel',

  // Pins
  pinsOnPage: (n: number) => (n === 1 ? '1 report on this page' : `${n} reports on this page`),
  yours: 'Yours',
  others: 'Others',
  closed: 'Closed',
  alreadyReported: 'Already reported. Add your own if you see something different.',
  openConversation: 'Open conversation',
};

export type Strings = typeof en;

const nl: Strings = {
  feedback: 'Feedback',
  close: 'Sluiten',
  tabNew: 'Nieuw',
  tabMine: 'Mijn feedback',
  pins: 'Pins',
  unreadReplies: (n) => (n === 1 ? '1 nieuw antwoord' : `${n} nieuwe antwoorden`),

  whoTitle: 'Wie geeft feedback?',
  whoIntro:
    'We gebruiken je e-mailadres om je de status te laten zien van wat je stuurt, en om je te laten weten wanneer het team antwoordt.',
  name: 'Naam',
  workEmail: 'Werk-e-mail',
  continue: 'Verder',
  savedLocally: 'Alleen in deze browser opgeslagen. Je kunt het later onderin de zijbalk wijzigen.',
  privacy:
    'Je naam en e-mailadres zijn zichtbaar voor het Hoppinger-team en het projectteam van dit product.',
  privacyLink: 'Privacyverklaring',
  cancel: 'Annuleren',
  change: 'Wijzigen',
  emailInvalid: 'Vul een volledig e-mailadres in.',
  pickWho: 'Kies wie in deze browser feedback geeft, of voeg iemand toe.',
  continueAs: 'Verder als',
  someoneElse: 'Iemand anders',
  forget: 'Uit deze browser verwijderen',
  optional: 'Optioneel',
  namePlaceholder: 'Wordt ingevuld vanuit je e-mail',
  nameHint: 'Ingevuld vanuit je e-mail. Het team kan het aanpassen.',
  viewLarger: 'Klik om te vergroten',
  closeImage: 'Afbeelding sluiten',
  captureFailed: 'Screenshot mislukt. Plak in plaats daarvan een afbeelding.',
  retry: 'Opnieuw',

  reportBug: 'Bug melden',
  requestFeature: 'Functie aanvragen',
  summary: 'Samenvatting',
  required: 'Verplicht',
  summaryPlaceholderBug: 'Bijvoorbeeld: Opslaan doet niets',
  summaryPlaceholderFeature: 'Wat zou je willen kunnen?',
  summaryMissing: 'Voeg een korte samenvatting toe, zodat het team je melding terugvindt.',
  story: 'Verhaal',
  yourIdea: 'Je idee',
  draftSaved: 'Concept opgeslagen',
  storyPlaceholderBug:
    'Vertel wat je deed, wat er gebeurde en wat je verwachtte. Voeg onderweg screenshots toe.',
  storyPlaceholderFeature:
    'Beschrijf wat je zou willen kunnen en waarom het je helpt. Voeg screenshots toe om te laten zien waar.',
  continueStory: 'Vertel verder…',
  bold: 'Vet',
  italic: 'Cursief',
  link: 'Link',
  linkAdd: 'Toevoegen',
  linkClose: 'Linkveld sluiten',
  linkHint: 'Enter voegt de link toe, Esc sluit het veld en zet de focus terug in de tekst.',
  element: 'Element',
  area: 'Gebied',
  screenshot: 'Screenshot',
  storyHint:
    'Screenshots komen waar je cursor staat. Je kunt ook afbeeldingen plakken (⌘V) of erin slepen.',
  limitReached: (max) =>
    `Je hebt ${max} afbeeldingen toegevoegd, het maximum. Verwijder er een om een nieuwe toe te voegen.`,
  remove: 'Verwijderen',
  pastedImage: 'Geplakte afbeelding',
  elementLabel: 'Element:',
  areaLabel: 'Gebied:',
  takingScreenshot: 'Screenshot maken…',
  pickElementInline: 'Klik op het element op de pagina.',
  pickAreaInline: 'Sleep over de pagina om een gebied te kiezen.',
  landsHere: 'De screenshot komt hier. Esc om te annuleren.',
  dropTitle: 'Loslaten om toe te voegen aan je verhaal',
  dropSub: (left, max) => `Komt bij de cursor · nog ${left} van ${max}`,
  imageUnreadable: 'Deze afbeelding kon niet worden gelezen. Probeer een PNG of JPEG.',
  screenshotFailed:
    'De screenshot kon niet worden gemaakt. Je kunt je feedback nog steeds versturen.',
  techTitle: 'Technische gegevens die worden meegestuurd',
  techSummary: (errors, failed, actions) =>
    `Pagina, browser en schermgrootte · ${errors} consolefout${errors === 1 ? '' : 'en'} · ${failed} mislukt${failed === 1 ? '' : 'e'} verzoek${failed === 1 ? '' : 'en'} · ${actions} recente actie${actions === 1 ? '' : 's'}`,
  send: 'Feedback versturen',
  sending: 'Versturen…',
  sendFailedTitle: 'Je feedback is niet verstuurd.',
  sendFailedBody: 'De verbinding viel weg. Je concept en screenshots zijn bewaard.',
  tryAgain: 'Opnieuw proberen',

  sentTitle: 'Bedankt, je feedback is verstuurd.',
  sentBody: 'Het team bekijkt het. Als ze een vraag hebben, zie je die onder Mijn feedback.',
  justNow: 'Zojuist',
  screenshots: (n) => (n === 1 ? '1 screenshot' : `${n} screenshots`),
  view: 'Bekijken',
  sendMore: 'Meer versturen',

  bug: 'Bug',
  idea: 'Idee',
  reportsCount: (n) => (n === 1 ? '1 melding' : `${n} meldingen`),
  refresh: 'Vernieuwen',
  emptyTitle: 'Je hebt nog geen feedback gestuurd.',
  emptyBody: 'Wat je vanuit deze browser stuurt, zie je hier, met de antwoorden van het team.',
  writeFeedback: 'Feedback schrijven',
  loadFailedTitle: 'Je feedback kon niet worden geladen.',
  loadFailedBody: 'Controleer je verbinding en probeer het opnieuw. Er is niets verloren.',
  back: 'Mijn feedback',
  sent: 'Verstuurd',
  whatYouSent: 'Wat je stuurde',
  noMessagesTitle: 'Nog geen berichten.',
  noMessagesBody: 'Het team kan je hier vragen stellen, en jij kunt details toevoegen.',
  you: 'Jij',
  newDivider: 'Nieuw',
  reply: 'Antwoord',
  replyPlaceholder: 'Beantwoord een vraag of voeg details toe',
  toSend: 'om te versturen',
  sendReply: 'Antwoord versturen',
  replyFailed: 'Je antwoord kon niet worden verstuurd. Probeer het opnieuw.',

  bannerElement: 'Klik op het element dat je wilt melden',
  bannerArea: 'Sleep om een gebied van het scherm te kiezen',
  bannerTapElement: 'Tik op het element dat je wilt melden',
  bannerTapAgain: 'Tik nog eens om te bevestigen',
  bannerTapArea: 'Sleep over de pagina om een gebied te kiezen',
  esc: 'Esc',
  toCancel: 'om te annuleren',

  pinsOnPage: (n) => (n === 1 ? '1 melding op deze pagina' : `${n} meldingen op deze pagina`),
  yours: 'Van jou',
  others: 'Anderen',
  closed: 'Afgerond',
  alreadyReported: 'Al gemeld. Voeg je eigen melding toe als je iets anders ziet.',
  openConversation: 'Gesprek openen',
};

export type Locale = 'en' | 'nl';

let current: Strings = en;
let currentLocale: Locale = 'en';

export function setLocale(requested?: string): Locale {
  const lang = (requested ?? document.documentElement.lang ?? 'en').toLowerCase();
  currentLocale = lang.startsWith('nl') ? 'nl' : 'en';
  current = currentLocale === 'nl' ? nl : en;
  return currentLocale;
}

export const locale = () => currentLocale;

/** The active strings. Read at render time so a locale set in init() applies everywhere. */
export const t = (): Strings => current;

/** Status names the tester sees, translated where the backend uses our own words. */
const STATUS_NL: Record<string, string> = {
  Received: 'Ontvangen',
  'In review': 'In behandeling',
  Planned: 'Gepland',
  Done: 'Klaar',
  "Won't do": 'Wordt niet gedaan',
  'To Do': 'Te doen',
  'In Progress': 'Bezig',
};

export const statusLabel = (name: string) =>
  currentLocale === 'nl' ? (STATUS_NL[name] ?? name) : name;

export const dateLabel = (iso: string, withTime = false) =>
  new Intl.DateTimeFormat(currentLocale === 'nl' ? 'nl-NL' : 'en-GB', {
    day: 'numeric',
    month: 'short',
    ...(withTime ? { hour: '2-digit', minute: '2-digit' } : {}),
  }).format(new Date(iso));
